import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { RefreshCw, Lock, Activity } from "lucide-react";

const VERDICT_TONES = {
  allow: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  block: "border-red-500/40 text-red-300 bg-red-500/5",
};

export default function Telemetry() {
  const [items, setItems] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [filter, setFilter] = useState({ appliance_id: "", host: "", verdict: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.appliance_id) params.appliance_id = filter.appliance_id;
      if (filter.host) params.host = filter.host;
      if (filter.verdict) params.verdict = filter.verdict;
      const { data } = await api.get("/web/transactions", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    api.get("/appliances").then(({ data }) => {
      setAppliances(Array.isArray(data) ? data : []);
    }).catch(() => setAppliances([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { allow: 0, block: 0, total: items.length };
    for (const it of items) {
      if (it.verdict === "block") c.block++;
      else c.allow++;
    }
    return c;
  }, [items]);

  return (
    <Layout title="Telemetry" subtitle="WEB TRANSACTIONS · AES-256-GCM STREAM">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
          {counts.total} TXN · <span className="text-emerald-400">{counts.allow} ALLOW</span>
          {" · "}<span className="text-red-400">{counts.block} BLOCK</span>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> REFRESH
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="telemetry-filters">
        <div>
          <Label>Appliance</Label>
          <select
            data-testid="filter-appliance"
            value={filter.appliance_id}
            onChange={(e) => setFilter({ ...filter, appliance_id: e.target.value })}
            className="w-full h-9 bg-zinc-900 border border-zinc-800 rounded-sm px-2 font-mono text-sm"
          >
            <option value="">— all sources —</option>
            {appliances.map((a) => (
              <option key={a.appliance_id} value={a.appliance_id}>
                {a.appliance_id} ({a.cloud}/{a.region})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Host (substring not supported, exact match)</Label>
          <Input
            data-testid="filter-host"
            value={filter.host}
            onChange={(e) => setFilter({ ...filter, host: e.target.value })}
            placeholder="example.com"
            className="bg-zinc-900 border-zinc-800 rounded-sm font-mono"
          />
        </div>
        <div>
          <Label>Verdict</Label>
          <select
            data-testid="filter-verdict"
            value={filter.verdict}
            onChange={(e) => setFilter({ ...filter, verdict: e.target.value })}
            className="w-full h-9 bg-zinc-900 border border-zinc-800 rounded-sm px-2 font-mono text-sm"
          >
            <option value="">— any —</option>
            <option value="allow">allow</option>
            <option value="block">block</option>
          </select>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-hidden" data-testid="telemetry-list">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
              <th className="py-2 px-3">ts</th>
              <th className="py-2 px-3">verdict</th>
              <th className="py-2 px-3">method</th>
              <th className="py-2 px-3">host</th>
              <th className="py-2 px-3">path</th>
              <th className="py-2 px-3">src_ip</th>
              <th className="py-2 px-3">appliance</th>
              <th className="py-2 px-3">bytes</th>
            </tr>
          </thead>
          <tbody className="font-mono text-[11px]">
            {items.map((it) => (
              <tr key={it.txn_id} data-testid={`txn-${it.txn_id}`} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                <td className="py-1.5 px-3 text-zinc-400 whitespace-nowrap">{it.ts?.slice(0, 19).replace("T", " ")}</td>
                <td className="py-1.5 px-3">
                  <span className={`px-1.5 py-0.5 border rounded-sm uppercase text-[10px] ${VERDICT_TONES[it.verdict] || "border-zinc-700 text-zinc-400"}`}>
                    {it.verdict || "?"}
                  </span>
                </td>
                <td className="py-1.5 px-3 text-zinc-200">{it.method}</td>
                <td className="py-1.5 px-3 text-zinc-200 whitespace-normal break-words max-w-[16rem]">{it.host}</td>
                <td className="py-1.5 px-3 text-zinc-400 whitespace-normal break-words max-w-[20rem]">{it.path}</td>
                <td className="py-1.5 px-3 text-zinc-500">{it.src_ip}</td>
                <td className="py-1.5 px-3 text-cyan-400">
                  {it.appliance_id ? (
                    <span className="inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> {it.appliance_id}
                    </span>
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </td>
                <td className="py-1.5 px-3 text-zinc-400 whitespace-nowrap">
                  {(it.bytes_in || 0)} / {(it.bytes_out || 0)}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="py-6 px-3 text-center text-zinc-500 font-mono text-xs">
                <Activity className="w-4 h-4 inline mr-1" /> no transactions match this filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

function Label({ children }) {
  return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{children}</div>;
}
